import { FormEvent, useContext, useEffect, useMemo, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import {
  DataTable,
  DataTableExpandedRows,
  DataTableValueArray,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { Divider } from "primereact/divider";
import { useTallyFormQuestions } from "../../api/queries/tally/useTallyFormQuestions";
import { appToastContext } from "../../context/toast";
import { TallyQuestion } from "../../api/types/tally";
import { Dropdown } from "primereact/dropdown";
import { useFieldTypesFlat } from "../../api/queries/field-types/useFieldTypesFlat";
import { FieldType } from "../../api/types/field-type";
import { useTallyMapping } from "../../api/queries/tally/useTallyMapping";
import {
  TallyMappingPayload,
  TallyQuestionMappingItem,
  TALLY_MAPPING_FIELDS,
} from "../../api/types/tally";
import { useAccountList } from "../../api/queries/account/useAccountList";
import { useNavigate } from "react-router-dom";
import { MultiSelect } from "primereact/multiselect";
import { useTagList } from "../../api/queries/tag/list";
import MappingCompatibilityHelp from "./MappingCompatibilityHelp";
import { isTallyMappingCompatible } from "./compatibility";
import { getTallyQuestionType } from "./questionType";

interface FieldTypeOption {
  label: string;
  value: string;
  type: string;
  required: boolean;
}

interface TallyQuestionRow extends TallyQuestion {
  selectedFieldTypeId: string | null;
  selectedTagIds: string[];
}

export default function TallyFormQuestions() {
  const [formId, setFormId] = useState("");
  const [formName, setFormName] = useState("");
  const [accountId, setAccountId] = useState("");
  const [lastFetchedFormId, setLastFetchedFormId] = useState<string | null>(
    null,
  );
  const [selectedFieldTypes, setSelectedFieldTypes] = useState<
    Record<string, string | null>
  >({});
  const [selectedTags, setSelectedTags] = useState<Record<string, string[]>>(
    {},
  );
  const [invalidQuestionIds, setInvalidQuestionIds] = useState<Set<string>>(
    new Set(),
  );
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray
  >([]);
  const [isSaving, setIsSaving] = useState(false);
  const { ref: appToastRef } = useContext(appToastContext);
  const navigate = useNavigate();
  const tallyFormQuestions = useTallyFormQuestions();
  const tallyMapping = useTallyMapping();
  const accountListParams = useMemo(
    () => ({
      offset: 0,
      limit: 100,
    }),
    [],
  );
  const { data: accountList, isLoading: isAccountsLoading } =
    useAccountList(accountListParams);
  const {
    data: allFieldTypesResponse,
    isLoading: isFieldTypesLoading,
    refetch: refetchFieldTypes,
  } = useFieldTypesFlat({ enabled: false });
  const { data: tagListResponse } = useTagList();

  const questions = useMemo<TallyQuestion[]>(() => {
    return tallyFormQuestions.data?.data?.questions ?? [];
  }, [tallyFormQuestions.data]);

  const questionRows = useMemo<TallyQuestionRow[]>(
    () =>
      questions.map((question) => ({
        ...question,
        selectedFieldTypeId: selectedFieldTypes[question.id] ?? null,
        selectedTagIds: selectedTags[question.id] ?? [],
      })),
    [questions, selectedFieldTypes, selectedTags],
  );

  const buildFieldTypeLabel = (fieldType: FieldType): string => {
    const segments: string[] = [];
    if (fieldType.parent?.name) {
      segments.push(fieldType.parent.name);
    }
    segments.push(fieldType.name);
    return segments.join(" ▸ ");
  };

  const fieldTypeOptions = useMemo<FieldTypeOption[]>(() => {
    return (
      allFieldTypesResponse?.data?.map((fieldType: FieldType) => ({
        label: buildFieldTypeLabel(fieldType),
        value: fieldType.id,
        type: fieldType.type,
        required: fieldType.required,
      })) ?? []
    );
  }, [allFieldTypesResponse?.data]);

  const fieldTypeOptionsByValue = useMemo(
    () =>
      new Map(
        fieldTypeOptions.map((option) => [option.value, option] as const),
      ),
    [fieldTypeOptions],
  );

  const accountOptions = useMemo(
    () =>
      accountList?.data?.results?.map((account) => ({
        label: account.name,
        value: account.id,
      })) ?? [],
    [accountList?.data?.results],
  );

  const tagOptions = useMemo(
    () =>
      tagListResponse?.data?.results?.map((tag) => ({
        label: tag.name,
        value: tag.id,
      })) ?? [],
    [tagListResponse?.data?.results],
  );

  useEffect(() => {
    if (!questions.length) {
      setSelectedFieldTypes({});
      setSelectedTags({});
      setInvalidQuestionIds(new Set());
      return;
    }

    setSelectedFieldTypes((prev) => {
      const next: Record<string, string | null> = {};
      questions.forEach((question) => {
        next[question.id] = prev[question.id] ?? null;
      });
      return next;
    });

    setSelectedTags((prev) => {
      const next: Record<string, string[]> = {};
      questions.forEach((question) => {
        next[question.id] = prev[question.id] ?? [];
      });
      return next;
    });
  }, [questions]);

  const showToast = (
    severity: "success" | "info" | "warn" | "error",
    summary: string,
  ) => {
    if (!appToastRef?.current) {
      return;
    }

    appToastRef.current.show({
      severity,
      summary,
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedFormId = formId.trim();

    if (!trimmedFormId) {
      showToast("warn", "Please enter a Tally form ID.");
      return;
    }

    try {
      const [, fieldTypesResult] = await Promise.all([
        tallyFormQuestions.mutateAsync(trimmedFormId),
        refetchFieldTypes(),
      ]);

      if (fieldTypesResult.error) {
        throw fieldTypesResult.error;
      }

      setLastFetchedFormId(trimmedFormId);
      setFormName("");
      setAccountId("");
      showToast("success", "Questions loaded.");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ?? "Unable to fetch Tally questions.";
      showToast("error", errorMessage);
    }
  };

  const handleFieldTypeChange = (
    questionId: string,
    value: string | null | undefined,
  ) => {
    setSelectedFieldTypes((prev) => ({
      ...prev,
      [questionId]: value ?? null,
    }));
    setInvalidQuestionIds((prev) => {
      if (!prev.has(questionId)) {
        return prev;
      }

      const next = new Set(prev);
      next.delete(questionId);
      return next;
    });
  };

  const handleTagsChange = (questionId: string, value: string[]) => {
    setSelectedTags((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const questionTitleTemplate = (rowData: TallyQuestion) => {
    return (
      <div>
        <div className="font-semibold">{rowData.title || "-"}</div>
        {rowData.description && (
          <small className="text-500">{rowData.description}</small>
        )}
      </div>
    );
  };

  const questionTypeTemplate = (rowData: TallyQuestion) => (
    <span className="uppercase text-sm">
      {getTallyQuestionType(rowData) || "-"}
    </span>
  );

  const questionRequiredTemplate = (rowData: TallyQuestion) => (
    <span className="text-sm">
      {rowData.required ? "Required" : "Optional"}
    </span>
  );

  const isQuestionFieldTypeInvalid = (rowData: TallyQuestionRow): boolean => {
    if (!rowData.selectedFieldTypeId) {
      return false;
    }

    const selectedFieldType = fieldTypeOptionsByValue.get(
      rowData.selectedFieldTypeId,
    );

    if (!selectedFieldType) {
      return invalidQuestionIds.has(rowData.id);
    }

    return (
      invalidQuestionIds.has(rowData.id) ||
      !isTallyMappingCompatible(
        getTallyQuestionType(rowData),
        selectedFieldType.type,
      )
    );
  };

  const handleSaveMappings = async () => {
    if (!lastFetchedFormId) {
      showToast("warn", "Load a Tally form before saving mappings.");
      return;
    }

    if (!formName.trim()) {
      showToast("warn", "Form name is required.");
      return;
    }

    if (!accountId.trim()) {
      showToast("warn", "Account ID is required.");
      return;
    }

    const mappingItems = questions
      .map((question) => {
        const fieldTypeId = selectedFieldTypes[question.id];
        if (!fieldTypeId) {
          return null;
        }

        return {
          [TALLY_MAPPING_FIELDS.QUESTION_ID]: question.id,
          [TALLY_MAPPING_FIELDS.QUESTION_TITLE]: question.title ?? "",
          [TALLY_MAPPING_FIELDS.QUESTION_TYPE]: getTallyQuestionType(question),
          [TALLY_MAPPING_FIELDS.FIELD_TYPE_ID]: fieldTypeId,
          tag_ids: selectedTags[question.id] ?? [],
        };
      })
      .filter((item) => item !== null) as TallyQuestionMappingItem[];

    if (!mappingItems.length) {
      showToast("warn", "Select at least one field type to map.");
      return;
    }

    const invalidIds = questionRows
      .filter((question) => isQuestionFieldTypeInvalid(question))
      .map((question) => question.id);

    if (invalidIds.length > 0) {
      setInvalidQuestionIds(new Set(invalidIds));
      showToast("error", `Fix ${invalidIds.length} incompatible mapping(s).`);
      return;
    }

    const payload: TallyMappingPayload = {
      [TALLY_MAPPING_FIELDS.ACCOUNT_ID]: accountId.trim(),
      [TALLY_MAPPING_FIELDS.FORM_ID]: lastFetchedFormId,
      [TALLY_MAPPING_FIELDS.FORM_NAME]: formName.trim(),
      mappings: mappingItems,
    };

    setIsSaving(true);
    try {
      await tallyMapping.mutateAsync(payload);
      showToast("success", "Tally form created successfully.");
      navigate("/tally/forms");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ?? "Unable to save mappings.";
      showToast("error", errorMessage);
      setIsSaving(false);
    }
  };

  const fieldTypeSelectorTemplate = (rowData: TallyQuestionRow) => {
    const isInvalid = isQuestionFieldTypeInvalid(rowData);

    return (
      <div className="flex flex-column gap-1">
        <Dropdown
          value={rowData.selectedFieldTypeId}
          options={fieldTypeOptions}
          optionLabel="label"
          optionValue="value"
          onChange={(event) => handleFieldTypeChange(rowData.id, event.value)}
          placeholder="Select field type"
          className={`w-full ${isInvalid ? "p-invalid" : ""}`}
          showClear
          filter
          filterBy="label"
          loading={isFieldTypesLoading}
          emptyMessage={
            isFieldTypesLoading
              ? "Loading field types..."
              : "No field types found"
          }
          itemTemplate={(option) =>
            option ? (
              <div className="flex flex-column">
                <span className="font-semibold">{option.label}</span>
                <small className="text-500 uppercase">
                  {option.type} {option.required ? "• Required" : ""}
                </small>
              </div>
            ) : null
          }
          valueTemplate={(option, props) => {
            const selectedOption =
              typeof option === "string"
                ? fieldTypeOptionsByValue.get(option)
                : option;

            if (selectedOption) {
              return (
                <div className="flex flex-column">
                  <span className="font-semibold">{selectedOption.label}</span>
                  <small className="text-500 uppercase">
                    {selectedOption.type}{" "}
                    {selectedOption.required ? "• Required" : ""}
                  </small>
                </div>
              );
            }

            return <span className="text-400">{props.placeholder}</span>;
          }}
        />
        {isInvalid && (
          <small className="p-error">
            Incompatible with {getTallyQuestionType(rowData)}
          </small>
        )}
      </div>
    );
  };

  const rowExpansionTemplate = (rowData: TallyQuestionRow) => {
    return (
      <div className="p-3">
        <label className="block text-500 mb-2">
          Default Tags (applied when answer is present)
        </label>
        <MultiSelect
          value={rowData.selectedTagIds}
          options={tagOptions}
          onChange={(event) => handleTagsChange(rowData.id, event.value)}
          placeholder="Select tags"
          className="w-full md:w-30rem"
          display="chip"
          filter
          maxSelectedLabels={5}
        />
      </div>
    );
  };

  return (
    <div className="col-12">
      <div className="flex py-3 flex-column">
        <h1 className="m-0">Tally Form Questions</h1>
        <span className="text-500">
          Preview questions for a Tally form before mapping them to CRM fields.
        </span>
      </div>

      <div className="card p-4">
        <form
          onSubmit={handleSubmit}
          className="flex gap-3 flex-column flex-md-row align-items-start"
        >
          <span className="p-float-label flex-1 w-full">
            <InputText
              id="formId"
              value={formId}
              onChange={(event) => setFormId(event.target.value)}
              className="w-full"
              placeholder="Form ID"
            />
            <label htmlFor="formId">Tally form ID</label>
          </span>

          <Button
            type="submit"
            label="Fetch questions"
            className="p-button-main"
            loading={tallyFormQuestions.isLoading}
          />
        </form>

        <Divider />

        <div>
          <p className="m-0 text-sm text-500">
            Paste the ID from the Tally form URL (e.g.{" "}
            <code>https://tally.so/r/FORM_ID</code>) to inspect its questions.
          </p>
        </div>
      </div>

      {lastFetchedFormId && (
        <div className="card p-4 mt-4">
          <div className="flex flex-column flex-md-row align-items-md-center gap-2 mb-3">
            <div>
              <div className="flex align-items-center gap-2">
                <h2 className="m-0 text-lg">Questions</h2>
                <MappingCompatibilityHelp />
              </div>
              <small className="text-500">
                Form ID: <code>{lastFetchedFormId}</code>
              </small>
            </div>
            <div className="ml-auto text-right">
              <span className="font-medium">
                {questions.length} question{questions.length === 1 ? "" : "s"}
              </span>
            </div>
          </div>

          <div className="flex flex-column flex-md-row gap-3 mb-4">
            <div className="w-full md:w-20rem">
              <label htmlFor="accountId" className="block text-sm mb-2">
                Account *
              </label>
              <Dropdown
                id="accountId"
                value={accountId || null}
                options={accountOptions}
                onChange={(event) => setAccountId(event.value ?? "")}
                placeholder="Select account"
                className="w-full"
                showClear
                filter
                loading={isAccountsLoading}
                emptyMessage={
                  isAccountsLoading
                    ? "Loading accounts..."
                    : "No accounts found"
                }
              />
            </div>
            <span className="p-float-label w-full md:w-30rem">
              <InputText
                id="formName"
                value={formName}
                onChange={(event) => setFormName(event.target.value)}
                className="w-full"
                placeholder="Form name"
                required
              />
              <label htmlFor="formName">Form name *</label>
            </span>
          </div>
          <small className="text-500 mb-3 block">
            Provide the exact Tally form name and target account before saving
            mappings.
          </small>

          <DataTable
            value={questionRows}
            emptyMessage={
              tallyFormQuestions.isLoading
                ? "Loading questions..."
                : "No questions found for this form."
            }
            loading={tallyFormQuestions.isLoading}
            stripedRows
            responsiveLayout="scroll"
            expandedRows={expandedRows}
            onRowToggle={(e) => setExpandedRows(e.data)}
            rowExpansionTemplate={rowExpansionTemplate}
            dataKey="id"
            rowClassName={(rowData: TallyQuestionRow) =>
              isQuestionFieldTypeInvalid(rowData) ? "tally-invalid-row" : ""
            }
          >
            <Column field="title" header="Title" body={questionTitleTemplate} />
            <Column
              field="type"
              header="Type"
              body={questionTypeTemplate}
              style={{ width: "10rem" }}
            />
            <Column
              field="required"
              header="Required"
              body={questionRequiredTemplate}
              style={{ width: "8rem" }}
            />
            <Column
              header="Field Type"
              body={fieldTypeSelectorTemplate}
              style={{ width: "22rem" }}
            />
            <Column
              body={(rowData) => {
                const hasTags = rowData.selectedTagIds.length > 0;

                return (
                  <Button
                    icon={`pi ${hasTags ? "pi-tags" : "pi-plus"}`}
                    label={
                      hasTags
                        ? `${rowData.selectedTagIds.length} Tags`
                        : "Add Tags"
                    }
                    className={
                      hasTags
                        ? "p-button-outlined p-button-sm"
                        : "p-button-text p-button-sm"
                    }
                    onClick={() => {
                      const newExpandedRows = Array.isArray(expandedRows)
                        ? [...expandedRows]
                        : [];
                      const index = newExpandedRows.findIndex(
                        (r: any) => r.id === rowData.id,
                      );
                      if (index >= 0) {
                        newExpandedRows.splice(index, 1);
                      } else {
                        newExpandedRows.push(rowData);
                      }
                      setExpandedRows(newExpandedRows);
                    }}
                  />
                );
              }}
              style={{ width: "10rem", textAlign: "center" }}
            />
          </DataTable>

          <div className="flex justify-content-end mt-4">
            <Button
              label="Save mappings"
              className="p-button-primary"
              onClick={handleSaveMappings}
              disabled={isSaving}
              loading={isSaving}
            />
          </div>
        </div>
      )}
    </div>
  );
}
